import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCustomerDetails from "../../data/useCustomerDetails";
import { useAuth } from "../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../config";
import useQuotes from "../../data/useQuotes";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { LeadStatus, QuoteTabType } from "../../models/Lead";
import QuoteCallStageFilter, {
  QUOTE_CALL_STAGES,
  QuoteCallStageKey,
} from "../../components/leads/quotes/QuoteCallStageFilter";
import QuotesTable from "../../components/leads/quotes/table/QuotesTable";
import QuoteStageFilter, { QuoteStage } from "../../components/leads/quotes/QuoteStageFilter";
import QuoteTypeTabs from "../../components/leads/quotes/QuoteTypeTabs";
import QuoteSearchFields from "../../components/leads/quotes/QuoteSearchFields";

export default function Quotes() {
  const { t } = useTranslation("leads");
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { customerId } = useAuth();
  const { features } = useCustomerDetails();
  // Only SAV can allocate ATF; other call centres are isolated within their own system.
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { quoteStats, invalidateQuoteStats } = useQuotes("QuotesPage");
  const [activeStageFilter, setActiveStageFilter] = useState<QuoteCallStageKey | null>(null);
  const [activeQuoteTab, setActiveQuoteTab] = useState<QuoteTabType>(QuoteTabType.Quotes);
  const [activeQuoteStage, setActiveQuoteStage] = useState<QuoteStage | null>(null);
  
  const [sortAdditionalBody, setSortAdditionalBody] = useState<Record<string, any>>({});

  const [tableTotalRows, setTableTotalRows] = useState<number | null>(null);
  const isCallCentre = useMemo(
    () => !!features.data?.data?.find((f) => f.name === "Call Centre"),
    [features.data]
  );

  useEffect(() => {
    if (!features.isLoading && !isCallCentre) {
      navigate("/", { replace: true });
      return;
    }

    setInfo({
      name: t("Quotes"),
      desc: t("View and manage call list quotes"),
    });
  }, [features.isLoading, isCallCentre, navigate, t]);

  const activeCallStage = useMemo(
    () => QUOTE_CALL_STAGES.find((stage) => stage.key === activeStageFilter) ?? null,
    [activeStageFilter]
  );

  const additionalBody = useMemo(() => {
    const callStatusFilter = activeCallStage?.isNullFilter
      ? {
        CallListStatus: null,
        CallListStatusIsNull: {
          FieldName: "CallListStatus",
        },
      }
      : {
        CallListStatus: activeCallStage?.callListStatus ?? null,
        CallListStatusIsNull: null,
      };

    return {
      Status: LeadStatus["Call List"],
      QuoteStep: activeQuoteStage,
      QuoteTabType: activeQuoteTab,
      AwaitingCod: true,
      IncludeCompleted: false,
      DocumentFilter: 6, // All
      ...callStatusFilter,
      ...sortAdditionalBody,
    };
  }, [activeCallStage, activeQuoteStage, activeQuoteTab, sortAdditionalBody]);

  const stageCounts = useMemo(() => {
    const stats = quoteStats.data?.data;
    if (!stats) return {};

    return {
      awaiting_contact: stats.awaitingContact,
      call_list_one: stats.callListOne,
      email_list_one: stats.emailListOne,
      call_list_two: stats.callListTwo,
      email_list_two: stats.emailListTwo,
      call_list_three: stats.callListThree,
      email_list_three: stats.emailListThree,
      manager_approval: stats.managerApproval,
      archived: stats.archived,
    };
  }, [quoteStats.data?.data]);

  const quoteStageCounts = useMemo(() => {
    const stats = quoteStats.data?.data;
    if (!stats) return {};

    return {
      1: stats.quoteStageOne,
      2: stats.quoteStageTwo,
      3: stats.quoteStageThree,
      4: stats.quoteStageFour,
      5: stats.quoteStageFive,
      6: stats.quoteStageSix,
      7: stats.quoteStageSeven,
    } as Partial<Record<QuoteStage, number>>;
  }, [quoteStats.data?.data]);

  const quoteTabCounts = useMemo(() => {
    const stats = quoteStats.data?.data;
    if (!stats) return {};

    return {
      [QuoteTabType.Quotes]: stats.quotesTab,
      [QuoteTabType.Atf]: stats.atfTab,
      [QuoteTabType.HighValue]: stats.highValueTab,
      [QuoteTabType.HighPriority]: stats.highPriorityTab,
      [QuoteTabType.ManagerAssistance]: stats.managerAssistanceTab,
    } as Partial<Record<QuoteTabType, number>>;
  }, [quoteStats.data?.data]);

  const selectedStageStatCount = useMemo(() => {
    if (!activeStageFilter) return null;
    const value = stageCounts[activeStageFilter];
    return typeof value === "number" ? value : null;
  }, [activeStageFilter, stageCounts]);

  const selectedQuoteStageStatCount = useMemo(() => {
    if (!activeQuoteStage) return null;
    const value = quoteStageCounts[activeQuoteStage];
    return typeof value === "number" ? value : null;
  }, [activeQuoteStage, quoteStageCounts]);

  const selectedQuoteTabStatCount = useMemo(() => {
    const value = quoteTabCounts[activeQuoteTab];
    return typeof value === "number" ? value : null;
  }, [activeQuoteTab, quoteTabCounts]);

  useEffect(() => {
    if (tableTotalRows === null) return;

    // Only compare when one stats dimension is selected.
    if (activeStageFilter && !activeQuoteStage && selectedStageStatCount !== null && tableTotalRows !== selectedStageStatCount) {
      invalidateQuoteStats();
      return;
    }

    if (activeQuoteStage && !activeStageFilter && selectedQuoteStageStatCount !== null && tableTotalRows !== selectedQuoteStageStatCount) {
      invalidateQuoteStats();
      return;
    }

    if (!activeStageFilter && !activeQuoteStage && selectedQuoteTabStatCount !== null && tableTotalRows !== selectedQuoteTabStatCount) {
      invalidateQuoteStats();
    }
  }, [
    activeStageFilter,
    activeQuoteStage,
    activeQuoteTab,
    tableTotalRows,
    selectedStageStatCount,
    selectedQuoteStageStatCount,
    selectedQuoteTabStatCount,
    invalidateQuoteStats,
  ]);

  if (!isCallCentre) return null;

  return (
    <div className="space-y-4">
      <QuoteCallStageFilter
        setActiveStageFilter={setActiveStageFilter}
        activeStageFilter={activeStageFilter}
        totalQuotes={quoteStats.data?.data?.total}
        stageCounts={stageCounts}
      />
      <QuoteStageFilter
        setActiveQuoteStage={setActiveQuoteStage}
        activeQuoteStage={activeQuoteStage}
        totalQuoteStages={quoteStats.data?.data?.totalQuoteStages}
        stageCounts={quoteStageCounts}
      /> 
      <QuoteTypeTabs
        activeTab={activeQuoteTab}
        setActiveTab={setActiveQuoteTab}
        tabCounts={quoteTabCounts}
        showAtfTab={isSavCustomer}
      />
      <QuoteSearchFields sortAdditionalBody={sortAdditionalBody} setSortAdditionalBody={setSortAdditionalBody} />
      <QuotesTable additionalBody={additionalBody} onTotalRowsChange={setTableTotalRows} />
    </div>
  );
}
